import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["enlargeButton", "video"]

  connect() {
    console.log("Video preview controller connected", this.element)
    
    // Reset button event listeners when controller connects
    // This ensures it works with Turbo navigation
    this.initButtons()
    
    // Register for Turbo events
    document.addEventListener('turbo:load', this.initButtons.bind(this))
    document.addEventListener('turbo:frame-load', this.initButtons.bind(this))
    document.addEventListener('turbo:render', this.initButtons.bind(this))
  }
  
  disconnect() {
    // Clean up event listeners when disconnected
    document.removeEventListener('turbo:load', this.initButtons.bind(this))
    document.removeEventListener('turbo:frame-load', this.initButtons.bind(this))
    document.removeEventListener('turbo:render', this.initButtons.bind(this))
  }
  
  initButtons() {
    console.log("Initializing video enlarge buttons")
    // Find all buttons and ensure they have event handlers
    const buttons = document.querySelectorAll('.video-enlarge-btn')
    buttons.forEach(button => {
      // Remove any existing listeners to prevent duplicates
      button.removeEventListener('click', this.handleEnlargeClick)
      // Add fresh event listener
      button.addEventListener('click', this.handleEnlargeClick)
    })
  }
  
  // Bound event handler
  handleEnlargeClick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    // Make sure we have a valid target
    if (event && event.target) {
      this.openVideoModal(event)
    } else {
      console.error("Invalid click event or target")
    }
  }

  fullscreen(event) {
    event.preventDefault()
    console.log("Fullscreen method called via Stimulus action")
    this.openVideoModal(event)
  }
  
  openVideoModal(event) {
    // Safety check the event
    if (!event || !event.target) {
      console.error("Invalid event object")
      return
    }
    
    // Find the button that was clicked
    const button = event.target.closest('button')
    if (!button) {
      console.error("No button found in event", event)
      return
    }
    
    // Get video source directly from data attributes and validate
    const videoSrc = button.getAttribute('data-video-src')
    if (!videoSrc) {
      console.error("No data-video-src attribute found on button", button)
      return
    }
    
    const videoType = button.getAttribute('data-video-type') || 'video/mp4'
    
    console.log("Opening video", videoSrc, videoType)
    
    // ALWAYS use direct DOM manipulation for consistency
    this.openModalManually(videoSrc, videoType)
  }
  
  openModalManually(videoSrc, videoType) {
    // Update the modal's video source
    const modalVideo = document.getElementById('modalVideo')
    const modalSource = document.getElementById('modalSource')
    const modal = document.getElementById('videoModal')
    
    if (!modalVideo || !modalSource || !modal) {
      console.error("Could not find modal elements")
      return
    }
    
    // Store original body overflow style for debugging
    const originalBodyOverflow = document.body.style.overflow
    const originalBodyPadding = document.body.style.paddingRight
    console.log("Before opening modal - Body styles:", { 
      overflow: originalBodyOverflow || 'not set', 
      paddingRight: originalBodyPadding || 'not set',
      classes: document.body.className
    })
    
    // Update video source
    modalSource.setAttribute('src', videoSrc)
    modalSource.setAttribute('type', videoType)
    modalVideo.load()
    
    // Show modal
    modal.classList.add('show')
    modal.style.display = 'block'
    document.body.classList.add('modal-open')
    
    // Create backdrop
    let backdrop = document.querySelector('.modal-backdrop')
    if (!backdrop) {
      backdrop = document.createElement('div')
      backdrop.className = 'modal-backdrop fade show'
      document.body.appendChild(backdrop)
    }
    
    // Log body styles after opening modal
    console.log("After opening modal - Body styles:", { 
      overflow: document.body.style.overflow || 'not set', 
      paddingRight: document.body.style.paddingRight || 'not set',
      classes: document.body.className
    })
    
    // Dispatch custom event that modal is opened
    console.log("Dispatching video-modal-opened event")
    document.dispatchEvent(new CustomEvent('video-modal-opened', {
      bubbles: true,
      detail: { videoSrc }
    }))
    
    // Add close handler
    const closeButton = modal.querySelector('.btn-close')
    if (closeButton) {
      closeButton.onclick = () => {
        // Log body styles before closing modal
        console.log("Before closing modal - Body styles:", { 
          overflow: document.body.style.overflow || 'not set', 
          paddingRight: document.body.style.paddingRight || 'not set',
          classes: document.body.className
        })
        
        modal.style.display = 'none'
        modal.classList.remove('show')
        document.body.classList.remove('modal-open')
        
        // Remove backdrop
        if (backdrop) backdrop.remove()
        
        // Make sure body scrolling is enabled
        document.body.style.removeProperty('overflow')
        document.body.style.removeProperty('padding-right')
        
        // Log body styles after our fixes
        console.log("After closing modal and applying fixes - Body styles:", { 
          overflow: document.body.style.overflow || 'not set', 
          paddingRight: document.body.style.paddingRight || 'not set',
          classes: document.body.className
        })
        
        // Pause video
        if (modalVideo) modalVideo.pause()
        
        // Dispatch custom event that modal is closed
        console.log("Dispatching video-modal-closed event")
        document.dispatchEvent(new CustomEvent('video-modal-closed', {
          bubbles: true
        }))
      }
    }
    
    // Add direct escape key handler to this modal
    const escapeHandler = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        console.log("Escape key detected directly in video preview controller")
        
        // Log body styles before closing via escape
        console.log("Before closing modal (escape) - Body styles:", { 
          overflow: document.body.style.overflow || 'not set', 
          paddingRight: document.body.style.paddingRight || 'not set',
          classes: document.body.className
        })
        
        modal.style.display = 'none'
        modal.classList.remove('show')
        document.body.classList.remove('modal-open')
        
        // Remove backdrop
        if (backdrop) backdrop.remove()
        
        // Make sure body scrolling is enabled
        document.body.style.removeProperty('overflow')
        document.body.style.removeProperty('padding-right')
        
        // Log body styles after our fixes
        console.log("After closing modal (escape) - Body styles:", { 
          overflow: document.body.style.overflow || 'not set', 
          paddingRight: document.body.style.paddingRight || 'not set',
          classes: document.body.className
        })
        
        // Pause video
        if (modalVideo) modalVideo.pause()
        
        // Dispatch custom event that modal is closed
        console.log("Dispatching video-modal-closed event after Escape")
        document.dispatchEvent(new CustomEvent('video-modal-closed', {
          bubbles: true
        }))
        
        // Remove this escape handler
        document.removeEventListener('keydown', escapeHandler)
      }
    }
    
    // Add the escape handler
    document.addEventListener('keydown', escapeHandler)
  }
}